/**
 * -----------------------------------------------------------------------------
 * Crash Track Detail Page
 * -----------------------------------------------------------------------------
 * This page is used to show the details of a single user of the organization.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  setModalsData,
  TListingQueryParams,
  TModalsState,
} from "../../api/store/commonSlice";
import svgIcons from "../../assets/svg/svgIcons";
import { AdminFormFieldSubmit } from "../../components/admin/formFields";
import HeaderView from "../../components/admin/headerView";
import { APP_CONFIG } from "../../constants/constants";
import { ACL_NAMES, useACL } from "../../hooks/acl";
import { useAccountTimezone } from "../../hooks/timezone";
import { routeUrls } from "../../navigation/routeUrls";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
} from "../../utils/dateTime";
import { useLoggedInUserData } from "../../utils/user";
import { ROLE_ACCESS_LEVELS } from "../admin-details/role/constants";
import { onPrint } from "../overview/utils/overview";
import DetailsFilterSelector from "./common/details-filter-selector";
import { useCrashtrackContext } from "./context/crash-track.context";
import ScreenCrashTrackTabAccelerationGraph from "./crash-track-tab-acceleration-graph";
import ScreenCrashTrackTabAssetInfo from "./crash-track-tab-asset-info";
import ScreenCrashTrackTabEventReport from "./crash-track-tab-event-report";
import ScreenCrashTrackTabIncidentInfo from "./crash-track-tab-incident-info";
import ScreenCrashTrackTabIncidentMap from "./crash-track-tab-incident-map";
import ScreenCrashTrackTabMagnitudeGraph from "./crash-track-tab-magnitude-graph";
import ScreenCrashTrackTabStreetView from "./crash-track-tab-street-view";
import ScreenCrashTrackTabWeatherInfo from "./crash-track-tab-weather-info";

// following slugs are used with tab components and translations - modify with caution
export const crashtrack_tab_slugs = [
  "asset_info", // 0
  "incident_info", // 1
  "magnitude_graph", // 2
  "acceleration_graph", // 3
  "incident_map", // 4
  "street_view", // 5
  "weather_information", // 6
  "event_report", // 7
];

const ScreenAdminDetailCrashTrack = () => {
  const { assetCrashId } = useParams<{ assetCrashId: string }>(); // expected to be a combination of asset id and timestamp separated by `__`
  const [assetCrashIdAsset, assetCrashIdTimestamp] = assetCrashId?.split(
    "__"
  ) || ["", ""];

  const { t } = useTranslation("translation", {
    keyPrefix: "admins.crashtrack.detailsPage",
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { tzForDateFns } = useAccountTimezone();

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const userIsImpersonated = useSelector(
    (state: { commonReducer: { userIsImpersonated: boolean } }) =>
      state.commonReducer.userIsImpersonated
  );

  const thisOrgCrashtrackEnabled = useLoggedInUserData("crashtrackEnabled");
  const { getUserACLAccessLevel } = useACL();
  useEffect(() => {
    if (
      thisOrgCrashtrackEnabled !== "1" &&
      getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_CRASHTRACK) <
        ROLE_ACCESS_LEVELS.READ
    ) {
      navigate(routeUrls.dashboard);
    }
  }, []);

  const [printMode, setPrintMode] = useState<boolean>(false);

  const [tabsLoadedCount, setTabsLoadedCount] = useState(0);
  const TOTAL_TABS = crashtrack_tab_slugs.length;
  useEffect(() => {
    if (APP_CONFIG.DEBUG.ALL) console.log("===== TOTAL", tabsLoadedCount);
  }, [tabsLoadedCount]);

  const [activeTab, setActiveTab] = useState<string>(crashtrack_tab_slugs[0]);
  useEffect(() => {
    if (!assetCrashIdAsset || !assetCrashIdTimestamp) {
      // if either option is not selected, reset tabs to first value
      setActiveTab(crashtrack_tab_slugs[0]);
    }
  }, [assetCrashId, assetCrashIdTimestamp]);

  // fetching crash track data - for intial values of timestamp dropdown
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { crashTrack: crashTrackQueryParams } = listingQueryParams;

  // redirect back to the main list of redux store values are not set
  useEffect(() => {
    if (!crashTrackQueryParams.start_time || !crashTrackQueryParams.end_time) {
      navigate(routeUrls.dashboardChildren.crashTrack);
    }
  }, []);

  const MemoizedRightSection = useMemo(() => {
    const params = { assetCrashIdAsset, assetCrashIdTimestamp };
    switch (activeTab) {
      case crashtrack_tab_slugs[0]: // "asset_info":
        return <ScreenCrashTrackTabAssetInfo {...params} />;
      case crashtrack_tab_slugs[1]: // "incident_info",
        return <ScreenCrashTrackTabIncidentInfo {...params} />;
      case crashtrack_tab_slugs[2]: // "magnitude_graph",
        return <ScreenCrashTrackTabMagnitudeGraph {...params} />;
      case crashtrack_tab_slugs[3]: // "acceleration_graph",
        return <ScreenCrashTrackTabAccelerationGraph {...params} />;
      case crashtrack_tab_slugs[4]: // "incident_map",
        return <ScreenCrashTrackTabIncidentMap {...params} />;
      case crashtrack_tab_slugs[5]: // "street_view",
        return <ScreenCrashTrackTabStreetView {...params} />;
      case crashtrack_tab_slugs[6]: // "weather_information",
        return <ScreenCrashTrackTabWeatherInfo {...params} />;
      case crashtrack_tab_slugs[7]: // "event_report",
        return <ScreenCrashTrackTabEventReport {...params} />;
      default:
        return null;
    }
  }, [assetCrashIdAsset, assetCrashIdTimestamp, activeTab]);

  const onBackButtonClick = () => {
    navigate(routeUrls.dashboardChildren.crashTrack);
  };

  // this mechanism allows refreshing of time dropdown values properly
  const [assetChanged, setAssetChanged] = useState<boolean>(false);
  useEffect(() => {
    setAssetChanged(true);
    setTimeout(() => {
      setAssetChanged(false);
    }, 200);
  }, [assetCrashIdAsset]);

  const handlePrintClick = () => {
    setTabsLoadedCount(0); // reset
    setPrintMode(true);
    dispatch(
      setModalsData({
        ...modalsState,
        showPrimaryOverlay: true,
      })
    );
  };

  const handleTabLoadComplete = useCallback(() => {
    setTabsLoadedCount((prev) => prev + 1);
  }, []);

  // effect to monitor when all tabs are done
  useEffect(() => {
    if (
      printMode &&
      tabsLoadedCount >= TOTAL_TABS &&
      !!modalsState.showPrimaryOverlay
    ) {
      // intentional 1s delay to prevent interference with state updates
      setTimeout(() => {
        dispatch(
          setModalsData({
            ...modalsState,
            showPrimaryOverlay: false,
          })
        );
        // intentional 1s delay to close loader popup and
        // let widgets/section come in view before triggering print
        setTimeout(() => {
          onPrint();
        }, 1000);
      }, 1000);
    }
  }, [printMode, tabsLoadedCount, TOTAL_TABS, modalsState]);

  const printModeComponentParams = {
    assetCrashIdAsset,
    assetCrashIdTimestamp,
    onLoadComplete: handleTabLoadComplete,
    printMode: true,
  };

  return (
    <>
      <HeaderView
        title={t("heading")}
        showBackButton={true}
        backButtonCallback={onBackButtonClick}
        rightComponent={
          <AdminFormFieldSubmit
            label={t("print_report")}
            variant="primary-like"
            disabled={!assetCrashIdTimestamp}
            onClick={handlePrintClick}
          />
        }
      />
      {!printMode && (
        <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2 no-print`}>
          <div className="Xgrid Xgrid-cols-1 md:Xgrid-cols-[3fr_7fr] flex flex-col md:flex-row Xmd:items-start mt-2 py-2 gap-4">
            <div
              className={`md:w-72 md:min-w-72 md:max-w-72 bg-panel-gray2 rounded-md flex flex-col gap-4 py-4`}
            >
              {!assetChanged && (
                <DetailsFilterSelector
                  assetCrashIdAsset={assetCrashIdAsset}
                  assetCrashIdTimestamp={assetCrashIdTimestamp}
                />
              )}
              {
                // !!initialCrashTrackListForAsset &&
                // initialCrashTrackListForAsset?.length > 0 &&
                !!assetCrashIdTimestamp ? (
                  <div className="pt-4">
                    <h3 className="text-lg font-medium px-5">
                      {t("tabs_heading")}
                    </h3>
                    <div className="flex flex-col gap-1 mt-4">
                      {crashtrack_tab_slugs.map((tab_slug, i) => (
                        <button
                          className={`${
                            activeTab === tab_slug
                              ? "bg-accent-blue-pale"
                              : "enabled:hover:bg-accent-blue-paleO66"
                          } px-5 h-10 text-left`}
                          type="button"
                          key={`tab_button_${i}`}
                          onClick={() => setActiveTab(tab_slug)}
                        >
                          {t(`tab_${tab_slug}.heading`)}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null
              }
            </div>
            <div
              className={`flex-grow flex flex-col bg-panel-gray2 rounded-md`}
            >
              {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
                <div className="relative">
                  <pre className="text-[8px] absolute bottom-full left-36 z-map-popup pointer-events-none">
                    crashTrackQueryParams:
                    <br />
                    {JSON.stringify(crashTrackQueryParams, null, 2)}
                    <br />
                    start:{" "}
                    {formattedDateTimeBasedOnTimezone(
                      dbTimeToJsTime(
                        parseInt(`${crashTrackQueryParams?.start_time}`)
                      ),
                      tzForDateFns || APP_CONFIG.DEFAULT_TIMEZONE,
                      APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
                    )}
                    <br />
                    end:{" "}
                    {formattedDateTimeBasedOnTimezone(
                      dbTimeToJsTime(
                        parseInt(`${crashTrackQueryParams?.end_time}`)
                      ),
                      tzForDateFns || APP_CONFIG.DEFAULT_TIMEZONE,
                      APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
                    )}
                  </pre>
                </div>
              )}
              {
                // !!initialCrashTrackListForAsset &&
                // initialCrashTrackListForAsset?.length > 0 &&
                !!assetCrashIdTimestamp ? (
                  <>{MemoizedRightSection}</>
                ) : (
                  <div className="flex flex-col items-center justify-center h-48 text-center">
                    <p className="font-bold text-xl text-gray-400 px-8">
                      {t("validation_message")}
                    </p>
                  </div>
                )
              }
            </div>
          </div>
        </div>
      )}

      {/* Print Mode */}
      {printMode && !!assetCrashIdTimestamp && (
        <div
          className={
            `crash-track-print-mode ` + // class `crash-track-print-mode` defined in index.scss
            `absolute top-0 left-0 right-0 py-0${userIsImpersonated ? " mt-[40px]" : ""} flex flex-col gap-8 bg-white z-modal`
          }
        >
          <button
            type="button"
            className="no-print fixed z-modal-buzzer top-14 right-5 flex p-2 enabled:bg-[#FFDDDD] disabled:bg-gray-300 rounded-lg group enabled:hover:bg-field-error-dark enabled:text-field-error-dark disabled:text-heading-gray888 enabled:hover:text-white"
            onClick={() => setPrintMode(false)}
          >
            <span className="text-sm font-semibold px-2">
              {t("close_print_mode")}
            </span>
            <img
              src={svgIcons.CloseIcon}
              alt="close print more"
              className="size-5 opacity-50 group-enabled:group-hover:opacity-100 group-enabled:group-hover:grayscale-0 group-enabled:group-hover:brightness-0 group-enabled:group-hover:invert"
            />
          </button>
          <AdminFormFieldSubmit
            label={"Print"}
            variant="primary"
            buttonDimensionClass="h-9 w-full px-4"
            customWrapperClass="no-print fixed z-modal-buzzer top-14 right-48 me-2"
            onClick={() => {
              onPrint();
            }}
          />
          <ScreenCrashTrackTabAssetInfo {...printModeComponentParams} />
          <ScreenCrashTrackTabIncidentInfo {...printModeComponentParams} />
          <ScreenCrashTrackTabMagnitudeGraph {...printModeComponentParams} />
          <ScreenCrashTrackTabAccelerationGraph {...printModeComponentParams} />
          <ScreenCrashTrackTabIncidentMap {...printModeComponentParams} />
          <ScreenCrashTrackTabStreetView {...printModeComponentParams} />
          <ScreenCrashTrackTabWeatherInfo {...printModeComponentParams} />
          <ScreenCrashTrackTabEventReport {...printModeComponentParams} />
        </div>
      )}
    </>
  );
};

export default ScreenAdminDetailCrashTrack;
